<script lang="ts">
  /**
   * «Anlegget» — hovedstatus-seksjonen i potte-detaljen (design-handoff juni 2026).
   * Viser riggen live: klima-stripe, vekstlys (toggle + tidslinje), vannreservoar
   * med flottør, og pottene som oktagoner der fukten stiger nedenfra. Trykk på et
   * felt / tanken / LUKE-merket / vekstlys-kortet → bunn-ark med detaljer/handlinger.
   * 2.0 stor har to skuffer: de tegnes som to smale tanker i midten, venstre og høyre.
   */
  import { onMount, onDestroy } from 'svelte';
  import { supabase } from '../lib/supabase';
  import { potter } from '../lib/stores';
  import {
    jordfuktProsent,
    jordGrenser,
    kasseForm,
    feltFukter,
    fuktStatus,
    jordSparkline,
    formaterTidssiden,
    tilkoblingsStatus,
    VANN_TOM_MM,
    VANN_FULL_MM,
    type PotteOppsett,
  } from '../lib/utils';
  import { tankAvstand, type TankStatus } from '../lib/tanker';
  import { beregnDli } from '../lib/lys';
  import { kalibreringsMaalingFeil, vannKalibreringFeil } from '../lib/vannkalibrering';
  import { ppfdMaks, lysAvstander } from '../lib/settings';
  import { kassePpfd } from '../lib/lysavstand';
  import { lysVarighetMin, minutterInnILysvindu, iLysvindu as iLysvinduNaa } from '../lib/tid';
  import { beregnVpd } from '../lib/klima';
  import { hostingStatus, type HostingStatus } from '../lib/hosting';
  import { isoTilDato, datoTilIso, sjekkDatoer } from '../lib/plantinger';
  import { sensorOppsett, feltHarProbe } from '../lib/sensorer';
  import { visFeil, visOk } from '../lib/toast';
  import type {
    Plante,
    Potte,
    PotteCommand,
    PotteSensorData,
    PottePlanteFull,
  } from '../lib/database.types';
  import PotteViz from './Potte.svelte';
  import type { SerieId } from '../lib/grafer';
  import LysSheet from './LysSheet.svelte';
  import SolBue from './viz/SolBue.svelte';
  import VannTank from './viz/VannTank.svelte';
  import VekstBar from './viz/VekstBar.svelte';
  import Sheet from './Sheet.svelte';

  type SensorRad = {
    registrert_at: string | null;
    jord1: number | null;
    jord2: number | null;
    jord3: number | null;
    jord4: number | null;
  };
  type FeltData = {
    seksjon: number;
    slotLabel: string;
    rolle: 'foran' | 'bak' | 'hel' | 'kopp';
    plante: Plante | null;
    fukt: number | null;
    /** Har feltet en jordprobe (lib/sensorer.ts)? Uten den vises ingen fukt. */
    harProbe: boolean;
  };

  let {
    potte,
    sensor,
    command,
    lysKlar = true,
    tanker,
    oppsett,
    planter,
    pottePlanter,
    sensorHistorikk,
    onAddPlante,
    onFjernPlante,
    onToggleSkille,
    onLagreNotat,
    onLagreDato,
    onCommandLagret,
    onVisGraf,
    simulert = false,
  }: {
    potte: Potte;
    sensor: PotteSensorData | null;
    command: PotteCommand | null;
    /** Første vellykkede lesing kreves før lysstyringen kan brukes. */
    lysKlar?: boolean;
    /** Tankene med nivå og trend (lib/tanker.ts): én i 1.0, venstre og høyre skuff i stor. */
    tanker: TankStatus[];
    oppsett: PotteOppsett[];
    planter: Plante[];
    pottePlanter: PottePlanteFull[];
    sensorHistorikk: SensorRad[];
    onAddPlante: (seksjon: number) => void;
    onFjernPlante: (pottePlanteId: string) => void;
    onToggleSkille: (potteIdx: number, nyDelt: boolean) => void;
    /** Resolves true når notatet er lagret. */
    onLagreNotat: (pottePlanteId: string, tekst: string) => Promise<boolean>;
    /** Ny plantedato (ISO). Resolves true når den er lagret. Uten den kan datoen ikke endres. */
    onLagreDato?: (pottePlanteId: string, iso: string) => Promise<boolean>;
    onCommandLagret: () => void;
    /** Åpner grafarket med én serie valgt (jord1–4 eller vann). */
    onVisGraf?: (serie: SerieId) => void;
    /** Forhåndsvisning på syntetiske data — skjul ekte-skrivende kalibrering. */
    simulert?: boolean;
  } = $props();

  // ---------- klokke (driver tidslinje + sol-bue) ----------
  let now = $state(new Date());
  let nowTimer: ReturnType<typeof setInterval> | undefined;
  onMount(() => {
    nowTimer = setInterval(() => (now = new Date()), 60_000);
  });
  onDestroy(() => clearInterval(nowTimer));

  // ---------- jord-verdier per seksjon ----------
  function jordRaa(seksjon: number): number | null {
    if (!sensor) return null;
    return [sensor.jord1, sensor.jord2, sensor.jord3, sensor.jord4][seksjon - 1] ?? null;
  }

  // Fukt per felt kommer fra den DELTE feltFukter() (samme som PotteKort og
  // oversiktens varsler) — udelt potte snitter sine to prober, delt potte har
  // én probe per felt. Flat liste i samme rekkefølge som oppsettets plasser.
  // Hvilke sensorer kassa har. Avlesningene er alt nullet for resten (PotteDetalj).
  const sensOppsett = $derived(sensorOppsett(potte));
  // Kassas egen jordkalibrering, ellers de felles tallene.
  const kal = $derived(jordGrenser(potte));
  // 2.0 stor: fire kopper på rad (oppsettet fra PotteDetalj har alt «Kopp 1–4»).
  const erStor = $derived(kasseForm(potte) === 'stor');

  const pots = $derived.by(() => {
    const fukter = feltFukter(
      sensor ? [sensor.jord1, sensor.jord2, sensor.jord3, sensor.jord4] : [],
      oppsett.map((po) => po.delt),
      kal,
    );
    let feltIdx = 0;
    return oppsett.map((po) => {
      const felt: FeltData[] = po.plasser.map((plass) => {
        const pp = pottePlanter.find((p) => p.seksjon === plass.seksjon);
        return {
          seksjon: plass.seksjon,
          slotLabel: plass.rolle === 'hel' ? 'Hele potta' : plass.etikett,
          rolle: plass.rolle,
          plante: pp?.plante ?? null,
          fukt: fukter[feltIdx++] ?? null,
          harProbe: feltHarProbe(plass.seksjon, po.delt, sensOppsett),
        };
      });
      // I stor er «pottene» halvdelene av kassa, hver over sin skuff.
      const navn = erStor ? (po.potteNr === 1 ? 'Venstre halvdel' : 'Høyre halvdel') : `Potte ${po.potteNr}`;
      return { potteNr: po.potteNr, navn, delt: po.delt, felt };
    });
  });

  // ---------- vekstlys ----------
  let lysLagrer = $state(false);
  let sisteIntensitet = $state(100);
  $effect(() => {
    if (command && command.intensitet > 0) sisteIntensitet = command.intensitet;
  });

  const lysPaa = $derived(!!command && command.intensitet > 0);

  const lysVarighet = $derived(command ? lysVarighetMin(command.timer_on, command.timer_off) : 0);
  // Norsk tid (tid.ts), samme klokke som firmwaren — ikke telefonens tidssone.
  const lysRel = $derived(command ? minutterInnILysvindu(command.timer_on, now) : 0);
  // Samme kant som firmwaren (`on <= now < off`): i selve av-minuttet er lyset
  // alt slukket og vi skal ikke si «Lyser nå».
  const iLysVindu = $derived(lysPaa && !!command && iLysvinduNaa(command.timer_on, command.timer_off, now));
  const lysDli = $derived(beregnDli(command?.intensitet ?? 0, lysVarighet / 60, kassePpfd(potte.potte_id, $lysAvstander, $ppfdMaks)));

  function fmtVar(min: number): string {
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    if (h === 0) return `${m} min`;
    return m ? `${h} t ${m} min` : `${h} t`;
  }
  const lysCaption = $derived(
    !command || !lysPaa
      ? 'Slått av'
      : lysVarighet === 0
        ? // Firmwaren tolker lik på/av-tid som «ingen lystid» — si det rett ut.
          'Av hele døgnet · på- og av-tid er like'
        : iLysVindu
          ? `Lyser nå · av om ${fmtVar(lysVarighet - lysRel)}`
          : `Av nå · slår på ${command.timer_on}`,
  );

  async function toggleLys() {
    if (lysLagrer) return;
    lysLagrer = true;
    const nyIntensitet = lysPaa ? 0 : sisteIntensitet || 100;
    const { error } = await supabase.from('potte_commands').upsert(
      {
        potte_id: potte.potte_id,
        intensitet: nyIntensitet,
        timer_on: command?.timer_on ?? '07:00',
        timer_off: command?.timer_off ?? '23:00',
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'potte_id' },
    );
    lysLagrer = false;
    if (error) visFeil('Kunne ikke endre lyset — prøv igjen.');
    else onCommandLagret();
  }

  // ---------- vann ----------
  let visKalibrering = $state(false); // kalibrering er skjult bak en svak knapp i vann-arket

  // Luft-VPD fra temp + RH — vises som en slank stripe under temp/luftfukt.
  const vpd = $derived(beregnVpd(sensor?.temperatur, sensor?.luftfuktighet));

  // Frakoblet: siste avlesning er gammel. Da dempes de sensor-drevne visualene
  // og en stripe forklarer at tallene kan være utdaterte — ellers ser en 3 t
  // gammel «100 % vann» ut som live sannhet.
  const offline = $derived(tilkoblingsStatus(potte.har_sensorer, sensor?.registrert_at, now.getTime()) === 'frakoblet');

  // ---------- bunn-ark ----------
  type Aapent =
    | null
    | {
        type: 'felt';
        seksjon: number;
        pottePlanteId: string;
        plante: string;
        bildeUrl: string | null;
        emoji: string | null;
        slot: string;
        fukt: number | null;
        adc: number | null;
        hist: number[];
        notater: string | null;
        harProbe: boolean;
        plantetAt: string;
        dagerTilHosting: number | null;
        kategori: string | null;
        hosting: HostingStatus | null;
      }
    | { type: 'vann'; nr: 1 | 2 }
    | { type: 'lys' };
  let aapent = $state<Aapent>(null);
  // Tanken i vann-arket: reservoaret, eller venstre/høyre skuff i stor.
  const aapenTank = $derived.by(() => {
    const a = aapent;
    return a?.type === 'vann' ? (tanker.find((x) => x.tank.nr === a.nr) ?? null) : null;
  });
  const forbrukMaks = $derived(
    Math.max(0.1, ...(aapenTank?.trend.dagligForbruk.length ? aapenTank.trend.dagligForbruk : [0.1])),
  );
  const dagerTekst = $derived.by(() => {
    const d = aapenTank?.trend.dagerIgjen ?? null;
    return d === null ? '–' : d < 1 ? '<1' : String(Math.round(d));
  });
  let notatRediger = $state(false);
  let notatTekst = $state('');
  let notatLagrer = $state(false);
  // «Fjern» krever to trykk (samme mønster som «Sett i drift»): knappen står
  // rett ved «Bytt plante», og i testmodus slettes plantingen for godt.
  let bekreftFjern = $state(false);
  let bildeFeilet = $state(false); // felt-arkets plantefoto feilet → emoji-fallback
  const lukk = () => {
    aapent = null;
    notatRediger = false;
    datoRediger = false;
    bekreftFjern = false;
    visKalibrering = false;
    kalibreringsFeil = null;
  };

  function startNotat(naa: string | null) {
    notatTekst = naa ?? '';
    notatRediger = true;
  }
  async function lagreNotat(id: string) {
    if (notatLagrer) return;
    const tekst = notatTekst.trim();
    notatLagrer = true;
    const ok = await onLagreNotat(id, tekst);
    notatLagrer = false;
    // Feilet lagringen, står editoren åpen med teksten — før ble den lukket og
    // viste notatet som lagret, og teksten var borte neste gang arket åpnet.
    if (!ok) return;
    if (aapent?.type === 'felt' && aapent.pottePlanteId === id) {
      aapent = { ...aapent, notater: tekst || null };
    }
    notatRediger = false;
  }

  function feltTrykk(seksjon: number) {
    const pp = pottePlanter.find((p) => p.seksjon === seksjon);
    if (!pp) {
      onAddPlante(seksjon); // tomt felt → åpne plantevelger direkte
      return;
    }
    // Finn visnings-data (fukt/slot) fra pots for konsistens med oktagonen.
    let fukt: number | null = jordfuktProsent(jordRaa(seksjon), kal.torr, kal.vaat);
    let slot = '';
    let harProbe = false;
    for (const po of pots) {
      const f = po.felt.find((x) => x.seksjon === seksjon);
      if (f) {
        fukt = f.fukt;
        slot = f.slotLabel;
        harProbe = f.harProbe;
      }
    }
    const hist = jordSparkline(
      sensorHistorikk.map((r) => ({
        registrert_at: r.registrert_at,
        verdi: jordfuktProsent([r.jord1, r.jord2, r.jord3, r.jord4][seksjon - 1] ?? null, kal.torr, kal.vaat),
      })),
    );
    bildeFeilet = false;
    aapent = {
      type: 'felt',
      seksjon,
      pottePlanteId: pp.id,
      plante: pp.plante.navn,
      bildeUrl: pp.plante.bilde_url ?? null,
      emoji: pp.plante.emoji ?? null,
      slot,
      fukt,
      adc: jordRaa(seksjon),
      hist,
      notater: pp.notater,
      harProbe,
      plantetAt: pp.plantet_at,
      dagerTilHosting: pp.plante.dager_til_hosting,
      kategori: pp.plante.kategori,
      hosting: potte.i_drift
        ? hostingStatus(pp.plantet_at, pp.plante.dager_til_hosting, pp.plante.kategori)
        : null,
    };
    notatRediger = false;
    bekreftFjern = false;
    datoRediger = false;
  }

  // ---------- plantedato (i felt-arket) ----------
  let datoRediger = $state(false);
  let datoVerdi = $state('');
  let datoLagrer = $state(false);
  const datoFeil = $derived(datoRediger ? sjekkDatoer(datoVerdi, null) : null);

  function startDato(iso: string) {
    datoVerdi = isoTilDato(iso);
    datoRediger = true;
  }
  async function lagreDato(id: string) {
    if (!onLagreDato || datoLagrer || datoFeil || aapent?.type !== 'felt') return;
    const iso = datoTilIso(datoVerdi, aapent.plantetAt);
    datoLagrer = true;
    const ok = await onLagreDato(id, iso);
    datoLagrer = false;
    if (!ok) return;
    if (aapent?.type === 'felt' && aapent.pottePlanteId === id) {
      aapent = {
        ...aapent,
        plantetAt: iso,
        hosting: potte.i_drift ? hostingStatus(iso, aapent.dagerTilHosting, aapent.kategori) : null,
      };
    }
    datoRediger = false;
  }

  // ---------- vann-kalibrering (i reservoar-arket, per tank) ----------
  let kalibrerer = $state<'tom' | 'full' | null>(null);
  let kalibreringsFeil = $state<string | null>(null);
  let kalibreringStartet = $state(0);
  const lagretKalibreringFeil = $derived(aapenTank ? vannKalibreringFeil(
    aapenTank.tank.tomMm ?? VANN_TOM_MM, aapenTank.tank.fullMm ?? VANN_FULL_MM,
  ) : null);
  const maalingFeil = $derived(kalibreringsMaalingFeil(
    aapenTank ? tankAvstand(aapenTank.tank, sensor) : null, sensor?.registrert_at, kalibreringStartet, now.getTime(),
  ));

  function startKalibrering() {
    visKalibrering = !visKalibrering;
    kalibreringStartet = Date.now();
    now = new Date();
    kalibreringsFeil = null;
  }

  async function settKalibrering(type: 'tom' | 'full') {
    // Les tanken FØR noe venter: arket kan lukkes mens lagringen pågår.
    const tank = aapenTank?.tank;
    const mm = tank ? tankAvstand(tank, sensor) : null;
    if (!tank || simulert || kalibrerer !== null) return;
    kalibreringsFeil = kalibreringsMaalingFeil(mm, sensor?.registrert_at, kalibreringStartet, Date.now());
    if (kalibreringsFeil || mm == null) return;
    kalibreringsFeil = vannKalibreringFeil(
      type === 'tom' ? mm : tank.tomMm ?? VANN_TOM_MM,
      type === 'full' ? mm : tank.fullMm ?? VANN_FULL_MM,
    );
    if (kalibreringsFeil) return;
    kalibrerer = type;
    const kasseId = potte.id;
    const oppdatering: Partial<Potte> = type === 'tom' ? { [tank.tomFelt]: mm } : { [tank.fullFelt]: mm };
    try {
      const { data, error } = await supabase.from('potter').update(oppdatering).eq('id', kasseId).select('id').single();
      if (error) throw error;
      if (!data) throw new Error('Kalibreringen ble ikke lagret');
      potter.update((liste) => liste.map((p) => (p.id === kasseId ? { ...p, ...oppdatering } : p)));
      visOk(type === 'tom' ? 'Tom-nivå kalibrert' : 'Fullt-nivå kalibrert');
      // Neste endepunkt må bruke en ny avlesning etter at tanken er endret.
      visKalibrering = false;
    } catch (error) {
      console.error('Kalibrering feilet:', error);
      kalibreringsFeil = 'Lagring feilet — prøv igjen (sjekk at du er innlogget).';
    } finally {
      kalibrerer = null;
    }
  }

  const arkFukt = $derived(aapent?.type === 'felt' ? fuktStatus(aapent.fukt) : null);
</script>

<div class="flex flex-col gap-3">
  <!-- Frakoblet-stripe: tallene under er siste kjente avlesning, ikke live -->
  {#if offline}
    <div class="flex items-center gap-2 px-3 py-2 rounded-[12px] bg-sun/[0.08] border border-sun/25 stig" style="--d: 15ms">
      <span class="w-2 h-2 rounded-full bg-sun shrink-0"></span>
      <span class="text-[11.5px] text-sun leading-snug">
        Frakoblet · sist avlest {formaterTidssiden(sensor?.registrert_at)}. Tallene under kan være utdaterte.
      </span>
    </div>
  {/if}

  <!-- Klima-stripe -->
  {#if potte.har_sensorer && sensor && (sensor.temperatur !== null || sensor.luftfuktighet !== null)}
    <div class="flex gap-2.5 stig transition-opacity" class:opacity-50={offline} style="--d: 30ms">
      <div class="flex-1 flex items-center gap-[11px] card !rounded-[14px] px-3.5 py-[11px]">
        <div
          class="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0"
          style="background:rgba(232,112,42,0.14)"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#f0935a" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0Z"/></svg>
        </div>
        <div>
          <div class="label !text-[10.5px] !tracking-[0.05em]">Temp</div>
          <div class="font-display text-[21px] font-semibold leading-tight">
            {sensor.temperatur?.toFixed(1).replace('.', ',') ?? '–'}°
          </div>
        </div>
      </div>
      <div class="flex-1 flex items-center gap-[11px] card !rounded-[14px] px-3.5 py-[11px]">
        <div
          class="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0"
          style="background:rgba(59,130,196,0.14)"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6aa9e0" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5S5 13 5 15a7 7 0 0 0 7 7Z"/></svg>
        </div>
        <div>
          <div class="label !text-[10.5px] !tracking-[0.05em]">Luftfukt</div>
          <div class="font-display text-[21px] font-semibold leading-tight">
            {sensor.luftfuktighet?.toFixed(0) ?? '–'}%
          </div>
        </div>
      </div>
    </div>
    {#if vpd.kpa !== null}
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-[12px] bg-bg-subtle border border-border stig transition-opacity"
        class:opacity-50={offline}
        style="--d: 45ms"
        title="VPD = hvor tørst lufta gjør plantene (temp + luftfuktighet)"
      >
        <span class="w-2 h-2 rounded-full shrink-0" style="background:{vpd.farge}"></span>
        <span class="font-mono text-[10.5px] text-text-muted">
          VPD {vpd.kpa.toFixed(2).replace('.', ',')} kPa
        </span>
        <span class="text-[11px] text-right ml-auto" style="color:{vpd.farge}">{vpd.tekst}</span>
      </div>
    {/if}
  {/if}

  <!-- Vekstlys — hele kortet åpner lys-arket; toggle stopper propagering -->
  {#if !lysKlar}
    <div class="card px-[15px] py-3.5" role="status">
      <div class="text-[13px] font-semibold">Vekstlys</div>
      <p class="text-xs text-text-muted mt-2">Lysplan ukjent — venter på databasen</p>
    </div>
  {:else}
  <div
    class="card px-[15px] py-3.5 stig cursor-pointer hover:brightness-[1.06] transition-[filter] duration-200"
    style="--d: 60ms"
    role="button"
    tabindex="0"
    onclick={() => (aapent = { type: 'lys' })}
    onkeydown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        aapent = { type: 'lys' };
      }
    }}
    aria-label="Åpne lysinnstillinger"
  >
    <div class="flex items-center justify-between">
      <div>
        <div class="text-[13px] font-semibold">Vekstlys</div>
        <div class="font-mono text-[10.5px] text-text-muted mt-0.5">
          {command ? `${command.timer_on} – ${command.timer_off}` : 'Ikke satt'}
        </div>
      </div>
      <button
        class="w-11 h-[25px] rounded-full p-[3px] flex items-center transition-all duration-200 disabled:opacity-50 {lysPaa
          ? 'bg-leaf justify-end'
          : 'bg-border justify-start'}"
        onclick={(e) => {
          e.stopPropagation();
          toggleLys();
        }}
        disabled={lysLagrer}
        role="switch"
        aria-checked={lysPaa}
        aria-label="Skru vekstlyset {lysPaa ? 'av' : 'på'}"
      >
        <span class="w-[19px] h-[19px] rounded-full bg-bg block shadow"></span>
      </button>
    </div>

    <!-- Sol-bue + intensitet/DLI — samme visuelle språk som tank/potter -->
    <div class="mt-1.5" style="opacity:{lysPaa ? 1 : 0.45}; transition:opacity 0.2s">
      <div class="max-w-[215px] mx-auto">
        <SolBue
          timerOn={command?.timer_on ?? '07:00'}
          timerOff={command?.timer_off ?? '23:00'}
          {now}
        />
      </div>
      <div class="flex items-baseline justify-center gap-3 mt-0.5">
        <span class="font-display text-[26px] font-semibold leading-none">
          {command?.intensitet ?? 0}<span class="text-sm font-normal text-text-muted">%</span>
        </span>
        <span class="font-mono text-[11px] text-text-muted">
          {lysDli.toFixed(1).replace('.', ',')} DLI
        </span>
      </div>
    </div>
    <div class="text-center text-[11px] text-text-muted mt-1.5">{lysCaption}</div>
  </div>
  {/if}

  <!-- Pottene (vannreservoaret tegnes som tanken i midten — eget kort fjernet).
       Vises også uten sensorer: da kan man fortsatt administrere planter, bare
       uten fukt-/vann-data. -->
  <div class="stig" style="--d: 180ms">
    <div class="flex items-baseline justify-between mb-3">
      <div class="text-[13px] font-semibold">Pottene</div>
      <div class="font-mono text-[11px] text-text-dim">trykk på et felt</div>
    </div>
    <div class="relative">
      <div class="flex {tanker.length > 1 ? 'gap-14' : 'gap-11'} items-start">
        {#each pots as p (p.potteNr)}
          <PotteViz
            navn={p.navn}
            delt={p.delt}
            felt={p.felt}
            harSensor={potte.har_sensorer}
            kopper={erStor}
            onToggleSkille={() => onToggleSkille(p.potteNr - 1, !p.delt)}
            onFelt={feltTrykk}
          />
        {/each}
      </div>
      {#if pots.length > 1 && tanker.length > 0}
          <!-- Vannet tegnet som vertikale tanker i midten: fyller fra bunn (topp =
               100 %, bunn = 0 %), flottør på overflaten. Ett felles reservoar i 1.0,
               to skuffer i stor (venstre og høyre, uten %-merke: tallene står under).
               Trykk åpner vann-arket for den tanken. -->
          <div
            class="absolute left-1/2 top-[34px] bottom-0 -translate-x-1/2 flex gap-1 {tanker.length > 1 ? 'w-[48px]' : 'w-[30px]'}"
          >
            {#each tanker as t (t.tank.nr)}
              <button
                class="flex-1 min-w-0 h-full active:brightness-110 transition-opacity"
                class:opacity-50={offline}
                onclick={() => (aapent = { type: 'vann', nr: t.tank.nr })}
                aria-label="{t.tank.navn} {t.pct ?? '–'} prosent — trykk for detaljer og påfylling"
              >
                <VannTank pct={t.pct} visProsent={tanker.length === 1} />
              </button>
            {/each}
          </div>
        {/if}
      </div>
      {#if pots.length > 1 && tanker.length > 0}
        <div class="flex items-center justify-center gap-1.5 mt-3 font-mono text-[10px] text-text-dim">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#60a5fa" stroke="none"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z"/></svg>
          {#if tanker.length > 1}
            Venstre {tanker[0]!.pct ?? '–'} % · høyre {tanker[1]!.pct ?? '–'} % — trykk på en skuff
          {:else}
            Trykk på tanken i midten for vann og påfylling
          {/if}
        </div>
      {/if}
    </div>
  </div>

<!-- Felt-detalj -->
<Sheet open={aapent?.type === 'felt'} onClose={lukk} label="Planteplass">
  {#if aapent?.type === 'felt'}
    {@const f = aapent}
    <div class="flex items-start justify-between gap-3.5">
      <div class="flex items-start gap-3 min-w-0">
        {#if f.bildeUrl && !bildeFeilet}
          <img
            src={f.bildeUrl}
            alt={f.plante}
            onerror={() => (bildeFeilet = true)}
            class="w-11 h-11 rounded-lg object-cover shrink-0 bg-surface-raised mt-0.5"
          />
        {:else if f.emoji}
          <span class="text-3xl shrink-0 leading-none mt-1">{f.emoji}</span>
        {/if}
        <div class="min-w-0">
          <div class="font-display text-[25px] font-semibold leading-[1.05] line-clamp-2 break-words">{f.plante}</div>
          <div class="font-mono text-[11px] text-text-muted mt-1">
            {f.slot}{f.harProbe ? ' · jordfukt' : ''}
          </div>
        </div>
      </div>
      {#if f.harProbe}
        <div class="font-display text-[38px] font-semibold leading-none shrink-0" style="color:{arkFukt?.farge}">
          {f.fukt ?? '—'}{#if f.fukt !== null}%{/if}
        </div>
      {/if}
    </div>

    {#if f.harProbe}
      <div class="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-white/[0.04] text-xs">
        <span class="w-2 h-2 rounded-full" style="background:{arkFukt?.farge}"></span>{arkFukt?.tekst}
      </div>
    {/if}

    {#if f.hosting}
      <!-- Høsting-nedtelling med fremdriftslinje -->
      <div class="mt-4 flex items-center gap-3 p-3 rounded-xl bg-bg-subtle border border-border {f.hosting.klar ? 'border-leaf/40' : ''}">
        <span class="text-xl shrink-0">🧺</span>
        <div class="min-w-0 flex-1">
          <div class="text-[13px] font-medium {f.hosting.klar ? 'text-leaf-glow' : ''}">{f.hosting.tekst}</div>
          <div class="mt-2">
            <VekstBar
              prosent={f.hosting.prosent}
              klar={f.hosting.klar}
              pulser={f.hosting.klar && f.hosting.kontinuerlig}
              size="full"
            />
          </div>
          <div class="font-mono text-[10px] text-text-dim mt-1.5">
            {#if f.hosting.klar}
              {f.hosting.kontinuerlig
                ? `Høsteklar i ${f.hosting.dagerHosteklar} ${f.hosting.dagerHosteklar === 1 ? 'dag' : 'dager'} · klipp litt av gangen`
                : `Høsteklar · dag ${f.hosting.dagerPlantet}`}
            {:else}
              dag {f.hosting.dagerPlantet} av ~{f.hosting.dagerTilHosting} til første høst
            {/if}
          </div>
        </div>
      </div>
    {/if}

    <!-- Uten sensorer finnes ingen fukthistorikk — sju tomme stolper ville sett
         ut som «0 % hele uka». -->
    {#if f.harProbe}
      <div class="mt-5">
        <div class="flex items-baseline justify-between mb-2">
          <span class="text-[11px] text-text-muted">Siste 7 dager</span>
          {#if onVisGraf}
            <button
              class="text-[11px] text-leaf-glow hover:underline"
              onclick={() => { const s = `jord${f.seksjon}` as SerieId; lukk(); onVisGraf(s); }}
            >Se graf →</button>
          {:else}
            <span class="font-mono text-[10px] text-text-dim">jordfukt %</span>
          {/if}
        </div>
        <div class="flex items-end gap-1.5 h-14" role="img" aria-label="Jordfukt siste 7 dager: {f.hist.join(', ')} %">
          {#each f.hist as h, i (i)}
            <div class="flex-1 rounded-t-[3px] bg-[#323a52]" style="height: {Math.max(4, h)}%"></div>
          {/each}
        </div>
      </div>
    {/if}

    <div class="flex items-start gap-2.5 mt-5 p-3 rounded-xl bg-bg-subtle border border-border">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 mt-px"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z"/></svg>
      <div class="text-[12px] text-text-muted leading-snug">
        Selvvannet via veke fra reservoaret. Fukten styres ikke per felt — fyll reservoaret når det er lavt.
      </div>
    </div>

    <!-- Plantedato: kan rettes, f.eks. etter «Ny runde» eller hvis du sådde i går.
         Skjult i simulering, der datoen er syntetisk. -->
    {#if onLagreDato && !simulert}
      <div class="mt-5">
        {#if datoRediger}
          <div class="flex items-center gap-2">
            <label for="plantetdato" class="text-xs text-text-muted shrink-0">📅 Plantet</label>
            <input
              id="plantetdato"
              type="date"
              bind:value={datoVerdi}
              max={isoTilDato(new Date().toISOString())}
              class="input !py-1.5 flex-1 min-w-0"
            />
          </div>
          {#if datoFeil}
            <p class="text-[12px] text-rose mt-1.5">{datoFeil}</p>
          {/if}
          <div class="flex gap-2 mt-2 justify-end">
            <button class="btn-ghost text-xs !px-3 !py-2" onclick={() => (datoRediger = false)}>Avbryt</button>
            <button
              class="btn-secondary text-xs !px-3 !py-2"
              disabled={datoLagrer || !!datoFeil}
              onclick={() => lagreDato(f.pottePlanteId)}
            >
              {datoLagrer ? 'Lagrer…' : 'Lagre dato'}
            </button>
          </div>
        {:else}
          <button
            class="text-xs text-text-muted hover:text-text transition-colors"
            onclick={() => startDato(f.plantetAt)}
            aria-label="Endre plantedato for {f.plante}"
          >
            📅 Plantet {new Date(f.plantetAt).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' })}
            <span class="text-text-dim">· endre</span>
          </button>
        {/if}
      </div>
    {/if}

    <div class={onLagreDato && !simulert ? 'mt-3' : 'mt-5'}>
      {#if notatRediger}
        <textarea
          bind:value={notatTekst}
          rows="2"
          placeholder="Notat — f.eks. «sådde 5 frø, spirte 4. juni»"
          class="input !text-sm resize-none"
          aria-label="Notat for {f.plante}"
        ></textarea>
        <div class="flex gap-2 mt-2 justify-end">
          <button class="btn-ghost text-xs !px-3 !py-2" onclick={() => (notatRediger = false)}>Avbryt</button>
          <button
            class="btn-secondary text-xs !px-3 !py-2"
            disabled={notatLagrer}
            onclick={() => lagreNotat(f.pottePlanteId)}
          >
            {notatLagrer ? 'Lagrer…' : 'Lagre notat'}
          </button>
        </div>
      {:else if f.notater}
        <button
          class="text-left text-sm text-text-muted italic hover:text-text transition-colors"
          onclick={() => startNotat(f.notater)}
        >
          📝 {f.notater}
        </button>
      {:else}
        <button class="text-xs text-text-dim hover:text-text transition-colors" onclick={() => startNotat(null)}>
          + Legg til notat
        </button>
      {/if}
    </div>

    {#if bekreftFjern}
      <div class="mt-5 text-[12px] text-rose text-center leading-snug">
        {potte.i_drift
          ? `Fjerne ${f.plante}? Den flyttes til historikken.`
          : `Fjerne ${f.plante}? I testmodus slettes den helt.`}
      </div>
    {/if}
    <div class="flex gap-2.5 {bekreftFjern ? 'mt-2' : 'mt-5'}">
      <!-- Les f-feltene FØR lukk(): `f` er en reaktiv {@const}; etter at lukk()
           setter aapent=null, vil `f` re-evalueres til null og kaste TypeError. -->
      {#if bekreftFjern}
        <button class="btn-secondary flex-1" onclick={() => (bekreftFjern = false)}>Avbryt</button>
        <button class="btn-danger flex-1" onclick={() => { const id = f.pottePlanteId; lukk(); onFjernPlante(id); }}>
          Ja, fjern
        </button>
      {:else}
        <button class="btn-secondary flex-1" onclick={() => { const s = f.seksjon; lukk(); onAddPlante(s); }}>Bytt plante</button>
        <button class="btn-danger flex-1" onclick={() => (bekreftFjern = true)}>Fjern</button>
      {/if}
    </div>
    <button class="btn-secondary w-full mt-2.5" onclick={lukk}>Lukk</button>

    {#if f.harProbe}
      <div class="mt-3.5 font-mono text-[10.5px] text-text-dim text-center">
        Probe {f.slot.toLowerCase()} · {f.adc ?? '—'} ADC (rå)
      </div>
    {/if}
  {/if}
</Sheet>

<!-- Reservoar-detalj (én tank, eller venstre/høyre skuff i stor) -->
<Sheet open={aapent?.type === 'vann'} onClose={lukk} label={aapenTank?.tank.navn ?? 'Vannreservoar'}>
  {#if aapenTank}
    {@const ts = aapenTank}
    <div class="font-display text-[25px] font-semibold leading-[1.05]">{ts.tank.navn}</div>
    <div class="font-mono text-[11px] text-text-muted mt-1">
      Lasermålt nivå · flottør{tanker.length > 1 ? ` · laser ${ts.tank.nr}` : ''}
    </div>
    {#if lagretKalibreringFeil}
      <p role="status" class="mt-3 rounded-xl border border-amber/30 bg-amber/10 p-3 text-sm text-amber">
        Vannnivået er ukjent til sensoren er kalibrert på nytt. {lagretKalibreringFeil}
      </p>
    {/if}
    {#if offline}
      <p class="mt-3 text-sm text-amber">Kassa er frakoblet. Tallene er fra siste avlesning, {formaterTidssiden(sensor?.registrert_at)}.</p>
    {/if}

    <div class="flex gap-2.5 mt-[18px]">
      <div class="flex-1 bg-bg-subtle border border-border rounded-xl py-3.5 px-2 text-center">
        <div class="font-display text-[26px] font-semibold leading-none text-sky">{ts.pct ?? '—'}%</div>
        <div class="font-mono text-[10px] text-text-muted mt-1.5">fylt</div>
      </div>
      <div class="flex-1 bg-bg-subtle border border-border rounded-xl py-3.5 px-2 text-center">
        <!-- Ukjent nivå er «–», ikke «0,0 liter» (trend regner null som 0). -->
        <div class="font-display text-[26px] font-semibold leading-none tabular-nums">
          {ts.pct === null ? '–' : ts.trend.literIgjen.toFixed(1).replace('.', ',')}
        </div>
        <div class="font-mono text-[10px] text-text-muted mt-1.5">liter igjen</div>
      </div>
      <div class="flex-1 bg-bg-subtle border border-border rounded-xl py-3.5 px-2 text-center">
        <!-- Under ett døgn igjen skal ikke rundes ned til «0 dager» (samme som kortet). -->
        <div class="font-display text-[26px] font-semibold leading-none tabular-nums">{dagerTekst}</div>
        <div class="font-mono text-[10px] text-text-muted mt-1.5">
          {dagerTekst === '<1' || dagerTekst === '1' ? 'dag igjen' : 'dager igjen'}
        </div>
      </div>
    </div>
    {#if onVisGraf}
      <!-- Les serien FØR lukk(): `ts` er en reaktiv {@const} og blir null når arket lukkes. -->
      <button class="btn-secondary w-full !py-2 mt-3" onclick={() => { const s = ts.tank.serie; lukk(); onVisGraf(s); }}>
        Se graf over vannstanden
      </button>
    {/if}

    {#if ts.trend.sistFyltAt}
      <div class="mt-3.5 text-[12px] text-text-muted">
        Sist fylt {ts.trend.sistFyltAt.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}.
      </div>
    {/if}

    {#if ts.trend.dagligForbruk.length > 0}
      <div class="mt-[18px]">
        <div class="flex items-baseline justify-between mb-2.5">
          <span class="text-[11px] text-text-muted">Forbruk siste 7 dager</span>
          <span class="font-mono text-[10px] text-text-dim">liter/dag</span>
        </div>
        <div class="flex items-end gap-1.5 h-[50px]">
          {#each ts.trend.dagligForbruk as d, i (i)}
            <div class="flex-1 rounded-t-[3px]" style="height:{Math.max(4, (d / forbrukMaks) * 100)}%; background:#3b82c4; opacity:0.85"></div>
          {/each}
        </div>
      </div>
    {/if}

    <button class="btn-secondary w-full mt-5" onclick={lukk}>Lukk</button>

    {#if !simulert}
      <div class="text-center mt-3">
        <p class="text-xs text-text-muted mb-2">
          Fyll eller tøm {tanker.length > 1 ? 'skuffen' : 'tanken'} først. Start deretter kalibreringen og vent på ny avlesning.
        </p>
        <button
          class="text-[11px] text-text-dim hover:text-text-muted transition-colors"
          onclick={startKalibrering}
        >
          {visKalibrering ? 'Skjul kalibrering' : 'Kalibrer sensor'}
        </button>
      </div>
      {#if visKalibrering}
        <div class="mt-2 p-3 rounded-xl bg-bg-subtle border border-border">
          <p class="text-[11px] text-text-muted leading-snug mb-2.5">
            Kontroller at flottøren går fritt og at råavstanden er stabil. ESP32 sender vanligvis hvert femte minutt. Målingen må være tatt etter at du åpnet kalibreringen.
          </p>
          {#if maalingFeil}
            <p role="status" class="text-xs text-amber mb-3">{maalingFeil}</p>
          {/if}
          <div class="flex gap-2.5">
            <button
              class="btn-secondary flex-1 !py-2 text-xs"
              disabled={kalibrerer !== null || maalingFeil !== null}
              onclick={() => settKalibrering('full')}
            >
              {kalibrerer === 'full' ? 'Lagrer…' : 'Sett som fullt nå'}
            </button>
            <button
              class="btn-secondary flex-1 !py-2 text-xs"
              disabled={kalibrerer !== null || maalingFeil !== null}
              onclick={() => settKalibrering('tom')}
            >
              {kalibrerer === 'tom' ? 'Lagrer…' : 'Sett som tomt nå'}
            </button>
          </div>
          <div class="mt-2.5 font-mono text-[10px] text-text-dim text-center tabular-nums">
            Avlest {tankAvstand(ts.tank, sensor) ?? '—'} mm ({formaterTidssiden(sensor?.registrert_at)}) · tom {ts.tank.tomMm ?? `${VANN_TOM_MM}*`} · full {ts.tank.fullMm ?? `${VANN_FULL_MM}*`}
          </div>
          {#if kalibreringsFeil}
            <div class="mt-2 p-2 rounded-md bg-rose/10 border border-rose/30 text-rose text-[11px]">{kalibreringsFeil}</div>
          {/if}
        </div>
      {/if}
    {/if}
  {/if}
</Sheet>

<!-- Vekstlys-detalj -->
<Sheet open={aapent?.type === 'lys'} onClose={lukk} label="Vekstlys">
  {#if aapent?.type === 'lys'}
    <LysSheet
      potteId={potte.potte_id}
      {planter}
      {command}
      {now}
      plantetDatoer={pottePlanter.map((p) => p.plantet_at)}
      onLagret={() => {
        onCommandLagret();
        lukk(); // «Ferdig» betyr ferdig — lukk arket når lagringen gikk bra
      }}
    />
  {/if}
</Sheet>
